import { useEffect, useRef, useState } from "react"
import type { PuzzleState } from "./usePuzzleState.ts"
import { saveLocal } from "../lib/puzzle-storage.ts"
import { saveProgress } from "../lib/api-client.ts"

export type SyncStatus = "idle" | "saving" | "saved" | "error"

export function useProgressSync(
  slug: string | undefined,
  state: PuzzleState,
  isAuthed: boolean,
) {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>("idle")
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const savedIndicatorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Track the version to avoid stale saves
  const versionRef = useRef(0)

  useEffect(() => {
    if (!slug || !state.puzzle) return

    // Increment version on every change
    versionRef.current++
    const currentVersion = versionRef.current

    // Save to localStorage immediately
    const now = new Date().toISOString()
    saveLocal(slug, {
      cells: state.cells,
      checkedCells: state.checkedCells,
      revealedCells: Array.from(state.revealedCells),
      currentCell: state.currentCell,
      direction: state.direction,
      isComplete: state.isComplete,
      updatedAt: now,
    })

    // Debounce DB sync (2 seconds)
    if (!isAuthed) return

    if (timerRef.current) clearTimeout(timerRef.current)
    if (savedIndicatorTimerRef.current) clearTimeout(savedIndicatorTimerRef.current)

    timerRef.current = setTimeout(async () => {
      // Skip if a newer version has been queued
      if (currentVersion !== versionRef.current) return

      setSyncStatus("saving")
      try {
        await saveProgress(slug, {
          cells: state.cells,
          checkedCells: state.checkedCells,
          revealedCells: Array.from(state.revealedCells),
          currentCell: state.currentCell,
          direction: state.direction,
          isComplete: state.isComplete,
        })
        // Only show "saved" if this is still the latest version
        if (currentVersion === versionRef.current) {
          setSyncStatus("saved")
          savedIndicatorTimerRef.current = setTimeout(() => setSyncStatus("idle"), 2000)
        }
      } catch {
        if (currentVersion === versionRef.current) {
          setSyncStatus("error")
        }
      }
    }, 2000)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [
    slug,
    state.cells,
    state.checkedCells,
    state.revealedCells,
    state.currentCell,
    state.direction,
    state.isComplete,
    state.puzzle,
    isAuthed,
  ])

  // Cleanup indicator timer on unmount
  useEffect(() => {
    return () => {
      if (savedIndicatorTimerRef.current) clearTimeout(savedIndicatorTimerRef.current)
    }
  }, [])

  return syncStatus
}
